import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { FileText, AlertCircle, CheckCircle, Upload, X } from 'lucide-react';
import { toast } from 'sonner';
import SEO from '../components/SEO';
import { submitQuote } from '../lib/api';

const initialState = {
  name: '', company: '', email: '', phone: '', preferredContact: 'Email',
  serviceType: '', collectionLocation: '', deliveryLocation: '', collectionDate: '',
  requiredDeliveryDate: '', incoterm: '', customsStatus: '',
  commodity: '', pieces: '', packaging: '', dimensions: '', weight: '', totalWeight: '', value: '', currency: 'GBP',
  aircraftReg: '', aircraftLocation: '', partNumber: '', partDescription: '', aogStatus: '', aogDeadline: '',
  dangerousGoods: 'no', unNumber: '', shippingName: '', dgClass: '', packingGroup: '', packingInstruction: '', netQuantity: '', sdsAvailable: 'no',
  tempControlled: 'no', tempRange: '', tempPackaging: '', tempMonitoring: '',
  additionalInfo: '', consent: false, website: '',
};

const Field = ({ label, name, value, onChange, type = 'text', required, placeholder, testid }) => (
  <div>
    <label className="block text-sm font-semibold mb-2">{label}{required && ' *'}</label>
    <input type={type} name={name} value={value} onChange={onChange} required={required} placeholder={placeholder} data-testid={testid} />
  </div>
);

const SectionTitle = ({ num, children }) => (
  <h2 className="text-2xl md:text-3xl font-bold mb-6 flex items-center gap-3 text-[#003057]">
    <span className="text-[#0089D0]">{num}</span>{children}
  </h2>
);

const Quote = () => {
  const [f, setF] = useState(initialState);
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);

  const onChange = (e) => {
    const { name, value, type, checked } = e.target;
    setF({ ...f, [name]: type === 'checkbox' ? checked : value });
  };

  const onFiles = (e) => {
    const selected = Array.from(e.target.files || []);
    setFiles((prev) => [...prev, ...selected].slice(0, 8));
  };

  const removeFile = (idx) => setFiles(files.filter((_, i) => i !== idx));

  const isAOG = f.serviceType === 'aog';
  const isDG = f.dangerousGoods === 'yes';
  const isTemp = f.tempControlled === 'yes';

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!f.consent) { toast.error('Please accept the privacy policy to continue.'); return; }
    setLoading(true);
    try {
      const payload = { ...f, dangerousGoods: f.dangerousGoods === 'yes' ? 'Yes' : 'No', tempControlled: undefined };
      const res = await submitQuote(payload, files);
      if (res.success) {
        toast.success(`Quote request received! Your reference is ${res.reference}. We'll respond shortly.`);
        setF(initialState);
        setFiles([]);
      } else {
        toast.error('Something went wrong. Please call our 24/7 line.');
      }
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Submission failed. Please try again or call us directly.';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <SEO title="Request a Freight Quote | Aero Time Critical" description="Request a competitive quote for time-critical, AOG, air, road and sea freight. Fast response, 24/7." path="/quote" />
      {/* Hero */}
      <section className="relative pt-24 min-h-[55vh] flex items-center overflow-hidden">
        <div className="absolute inset-0 z-0">
          <div className="absolute inset-0 bg-gradient-to-r from-[#002240]/95 via-[#003057]/90 to-[#003057]/70 z-10" />
          <img
            src="https://customer-assets-m6fa6gv7.emergentagent.net/job_cargo-urgent/artifacts/esv8b4gm_AOG%20ATC%20.png"
            alt="Aero Time Critical branded delivery van beside an aircraft"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="relative z-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full py-16">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} className="max-w-3xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 bg-[#C9A227] text-[#002240] px-4 py-2 rounded-full text-sm font-semibold mb-6">
              <FileText size={16} /> FAST RESPONSE GUARANTEED
            </div>
            <h1 className="text-5xl md:text-6xl font-bold mb-6 text-white">Request a <span className="text-[#00A3E0]">Quote</span></h1>
            <p className="text-xl text-gray-200 leading-relaxed">Complete the form below and our team will provide a competitive quote quickly. Additional fields appear based on your service.</p>
          </motion.div>
        </div>
      </section>

      {/* Form */}
      <section className="bg-[#F7F9FB] py-24">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="bg-white p-8 md:p-12 border border-[#E5E9EF] rounded-lg shadow-sm">
            <form onSubmit={handleSubmit} className="space-y-12">
              {/* Contact */}
              <div>
                <SectionTitle num="01">Contact Details</SectionTitle>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <Field label="Full Name" name="name" value={f.name} onChange={onChange} required testid="quote-name" placeholder="John Smith" />
                  <Field label="Company" name="company" value={f.company} onChange={onChange} required testid="quote-company" placeholder="Your Company Ltd" />
                  <Field label="Email Address" name="email" value={f.email} onChange={onChange} type="email" required testid="quote-email" placeholder="john@company.com" />
                  <Field label="Telephone" name="phone" value={f.phone} onChange={onChange} type="tel" required testid="quote-phone" placeholder="+44 1234 567890" />
                  <div>
                    <label className="block text-sm font-semibold mb-2">Preferred Contact Method</label>
                    <select name="preferredContact" value={f.preferredContact} onChange={onChange} data-testid="quote-preferred">
                      <option>Email</option><option>Telephone</option><option>WhatsApp</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Service */}
              <div>
                <SectionTitle num="02">Service Details</SectionTitle>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="md:col-span-2">
                    <label className="block text-sm font-semibold mb-2">Service Type *</label>
                    <select name="serviceType" value={f.serviceType} onChange={onChange} required data-testid="quote-service">
                      <option value="">Select service type</option>
                      <option value="aog">AOG / Time-Critical</option>
                      <option value="air">Air Freight</option>
                      <option value="road">Road Freight</option>
                      <option value="sea">Sea Freight</option>
                      <option value="courier">Onboard Courier</option>
                      <option value="charter">Aircraft Charter</option>
                    </select>
                  </div>
                  <Field label="Collection Location" name="collectionLocation" value={f.collectionLocation} onChange={onChange} required testid="quote-collection" placeholder="City, Country" />
                  <Field label="Delivery Location" name="deliveryLocation" value={f.deliveryLocation} onChange={onChange} required testid="quote-delivery" placeholder="City, Country" />
                  <Field label="Collection Ready Date/Time" name="collectionDate" value={f.collectionDate} onChange={onChange} type="datetime-local" testid="quote-collection-date" />
                  <Field label="Required Delivery Date/Time" name="requiredDeliveryDate" value={f.requiredDeliveryDate} onChange={onChange} type="datetime-local" required testid="quote-delivery-date" />
                  <Field label="Incoterm (if known)" name="incoterm" value={f.incoterm} onChange={onChange} placeholder="e.g., DAP, EXW" testid="quote-incoterm" />
                  <div>
                    <label className="block text-sm font-semibold mb-2">Customs Status</label>
                    <select name="customsStatus" value={f.customsStatus} onChange={onChange} data-testid="quote-customs">
                      <option value="">Select</option>
                      <option>Domestic (no customs)</option>
                      <option>Customs cleared</option>
                      <option>Customs coordination required</option>
                      <option>Unsure</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Cargo */}
              <div>
                <SectionTitle num="03">Cargo Details</SectionTitle>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="md:col-span-2">
                    <Field label="Commodity Description" name="commodity" value={f.commodity} onChange={onChange} required testid="quote-commodity" placeholder="e.g., Aircraft spare parts" />
                  </div>
                  <Field label="Number of Pieces" name="pieces" value={f.pieces} onChange={onChange} testid="quote-pieces" placeholder="e.g., 2" />
                  <Field label="Packaging Type" name="packaging" value={f.packaging} onChange={onChange} testid="quote-packaging" placeholder="e.g., Crate, Pallet, Box" />
                  <Field label="Dimensions (L x W x H cm, per piece)" name="dimensions" value={f.dimensions} onChange={onChange} testid="quote-dimensions" placeholder="100 x 80 x 60" />
                  <Field label="Weight per Piece (kg)" name="weight" value={f.weight} onChange={onChange} testid="quote-weight" placeholder="25" />
                  <Field label="Total Gross Weight (kg)" name="totalWeight" value={f.totalWeight} onChange={onChange} required testid="quote-total-weight" placeholder="50" />
                  <div className="grid grid-cols-2 gap-4">
                    <Field label="Declared Value" name="value" value={f.value} onChange={onChange} testid="quote-value" placeholder="10000" />
                    <div>
                      <label className="block text-sm font-semibold mb-2">Currency</label>
                      <select name="currency" value={f.currency} onChange={onChange} data-testid="quote-currency">
                        <option>GBP</option><option>EUR</option><option>USD</option>
                      </select>
                    </div>
                  </div>
                </div>
              </div>

              {/* AOG conditional */}
              {isAOG && (
                <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} className="bg-[#FFF8E6] border border-[#C9A227]/40 rounded-lg p-6">
                  <SectionTitle num="AOG">Aircraft-on-Ground Information</SectionTitle>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <Field label="Aircraft Registration / Tail Number" name="aircraftReg" value={f.aircraftReg} onChange={onChange} testid="quote-aircraft-reg" placeholder="e.g., G-ABCD" />
                    <Field label="Aircraft Location" name="aircraftLocation" value={f.aircraftLocation} onChange={onChange} testid="quote-aircraft-loc" placeholder="e.g., LHR" />
                    <Field label="Part Number" name="partNumber" value={f.partNumber} onChange={onChange} testid="quote-part-no" />
                    <Field label="Part Description" name="partDescription" value={f.partDescription} onChange={onChange} testid="quote-part-desc" />
                    <div>
                      <label className="block text-sm font-semibold mb-2">AOG Status</label>
                      <select name="aogStatus" value={f.aogStatus} onChange={onChange} data-testid="quote-aog-status">
                        <option value="">Select</option>
                        <option>AOG - Aircraft grounded</option>
                        <option>Critical - Preventative</option>
                        <option>Routine spare</option>
                      </select>
                    </div>
                    <Field label="Required Delivery Deadline" name="aogDeadline" value={f.aogDeadline} onChange={onChange} type="datetime-local" testid="quote-aog-deadline" />
                  </div>
                </motion.div>
              )}

              {/* DG */}
              <div>
                <SectionTitle num="04">Dangerous Goods</SectionTitle>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-semibold mb-2">Are the goods dangerous/hazardous? *</label>
                    <select name="dangerousGoods" value={f.dangerousGoods} onChange={onChange} required data-testid="quote-dg">
                      <option value="no">No</option>
                      <option value="yes">Yes</option>
                    </select>
                  </div>
                </div>
                {isDG && (
                  <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
                    <Field label="UN Number" name="unNumber" value={f.unNumber} onChange={onChange} testid="quote-un" placeholder="e.g., UN1263" />
                    <Field label="Proper Shipping Name" name="shippingName" value={f.shippingName} onChange={onChange} testid="quote-psn" />
                    <Field label="Class" name="dgClass" value={f.dgClass} onChange={onChange} testid="quote-dgclass" placeholder="e.g., 3" />
                    <Field label="Packing Group" name="packingGroup" value={f.packingGroup} onChange={onChange} testid="quote-pg" placeholder="I / II / III" />
                    <Field label="Packing Instruction (if known)" name="packingInstruction" value={f.packingInstruction} onChange={onChange} testid="quote-pi" />
                    <Field label="Net Quantity" name="netQuantity" value={f.netQuantity} onChange={onChange} testid="quote-netqty" />
                    <div>
                      <label className="block text-sm font-semibold mb-2">SDS Available?</label>
                      <select name="sdsAvailable" value={f.sdsAvailable} onChange={onChange} data-testid="quote-sds">
                        <option value="no">No</option><option value="yes">Yes</option>
                      </select>
                    </div>
                  </motion.div>
                )}
              </div>

              {/* Temperature */}
              <div>
                <SectionTitle num="05">Temperature Control</SectionTitle>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-semibold mb-2">Temperature-controlled required?</label>
                    <select name="tempControlled" value={f.tempControlled} onChange={onChange} data-testid="quote-temp">
                      <option value="no">No</option><option value="yes">Yes</option>
                    </select>
                  </div>
                </div>
                {isTemp && (
                  <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-6">
                    <Field label="Required Temperature Range" name="tempRange" value={f.tempRange} onChange={onChange} testid="quote-temprange" placeholder="e.g., 2-8°C" />
                    <Field label="Packaging Type" name="tempPackaging" value={f.tempPackaging} onChange={onChange} testid="quote-temppack" placeholder="e.g., Cool box" />
                    <div>
                      <label className="block text-sm font-semibold mb-2">Monitoring Required?</label>
                      <select name="tempMonitoring" value={f.tempMonitoring} onChange={onChange} data-testid="quote-tempmon">
                        <option value="">Select</option><option>Yes</option><option>No</option>
                      </select>
                    </div>
                  </motion.div>
                )}
              </div>

              {/* Documents */}
              <div>
                <SectionTitle num="06">Documents (optional)</SectionTitle>
                <p className="text-sm text-[#4A4A4A] mb-4">Upload commercial invoice, packing list, SDS, DG declaration or photos. Max 10MB per file (PDF, images, Office docs).</p>
                <label className="flex flex-col items-center justify-center border-2 border-dashed border-[#0089D0]/40 rounded-lg p-8 cursor-pointer hover:bg-[#F7F9FB] transition-colors">
                  <Upload className="text-[#0089D0] mb-2" size={28} />
                  <span className="text-[#003057] font-medium">Click to upload documents</span>
                  <input type="file" multiple onChange={onFiles} className="hidden" accept=".pdf,.jpg,.jpeg,.png,.webp,.doc,.docx,.xls,.xlsx,.csv,.txt" data-testid="quote-files" />
                </label>
                {files.length > 0 && (
                  <ul className="mt-4 space-y-2">
                    {files.map((file, idx) => (
                      <li key={idx} className="flex items-center justify-between bg-[#F7F9FB] px-4 py-2 rounded border border-[#E5E9EF]">
                        <span className="text-sm text-[#003057] truncate">{file.name}</span>
                        <button type="button" onClick={() => removeFile(idx)} className="text-red-500 hover:text-red-700" aria-label="Remove file"><X size={18} /></button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              <div>
                <label className="block text-sm font-semibold mb-2">Additional Instructions</label>
                <textarea name="additionalInfo" value={f.additionalInfo} onChange={onChange} rows={4} data-testid="quote-additional" placeholder="Any special handling requirements or additional details..." />
              </div>

              {/* Honeypot */}
              <input type="text" name="website" value={f.website} onChange={onChange} className="hidden" tabIndex={-1} autoComplete="off" aria-hidden="true" />

              {/* Info */}
              <div className="bg-[#0089D0]/10 border border-[#0089D0]/30 p-6 flex items-start gap-4 rounded-lg">
                <AlertCircle className="text-[#0089D0] flex-shrink-0 mt-1" size={24} />
                <div className="text-sm">
                  <p className="font-semibold mb-2 text-[#003057]">Quote Response Time</p>
                  <p className="text-[#4A4A4A]">Standard quotes: within 2 hours during business hours.<br />Urgent/AOG quotes: within 30 minutes, 24/7/365.</p>
                </div>
              </div>

              <label className="flex items-start gap-3 text-sm text-[#4A4A4A] cursor-pointer">
                <input type="checkbox" name="consent" checked={f.consent} onChange={onChange} required className="w-auto mt-1" data-testid="quote-consent" />
                <span>I consent to Aero Time Critical processing my data to provide a quotation, in line with the <a href="/privacy-policy" className="text-[#0089D0] underline">Privacy Policy</a>. *</span>
              </label>

              <motion.button type="submit" disabled={loading} whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }} className="btn-primary w-full text-xl py-4 flex items-center justify-center gap-3" data-testid="quote-submit">
                {loading ? 'Submitting...' : 'Request Quote'}
                <CheckCircle size={24} />
              </motion.button>

              <div className="text-center text-[#4A4A4A]">
                <p className="mb-2">Or contact us directly for immediate assistance:</p>
                <div className="flex flex-col sm:flex-row items-center justify-center gap-4 text-lg">
                  <a href="tel:+441753720055" className="text-[#0089D0] hover:underline font-semibold">+44 (0) 1753 720055</a>
                  <span className="hidden sm:inline text-[#4A4A4A]">•</span>
                  <a href="mailto:booking@aerotimecritical.co.uk" className="text-[#0089D0] hover:underline font-semibold">booking@aerotimecritical.co.uk</a>
                </div>
              </div>
            </form>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Quote;
